import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { DownloadedUser } from "./projectMessages";
import { dmThreadsApi } from "./dmThreads";
import { AcrScanStatus, AcrScanResult } from "../../types/acr";
import type { DmSystemContent } from "../../types/systemContent";

// ─── 型定義 ──────────────────────────────────────────

export interface DmMessageUser {
  id: number;
  display_name: string;
  avatar: string | null;
  position: "CLIENT" | "REVIEWER" | "ADMIN" | "PRODUCER" | "SPECIALIST" | "DIRECTOR" | null;
  seen_at?: string;
}

export interface DmMessageFile {
  id: string;
  file_key?: string | null;
  original_url: string | null;
  /** ダウンロード用 presigned URL（attachment 付き・短期有効。DL時はこちらを優先） */
  download_url?: string | null;
  file_name: string | null;
  file_type: string | null;
  file_metadata?: string | null;
  folder?: {
    id: string;
    name: string;
    parent_id: string | null;
  } | null;
  downloaded_users?: DownloadedUser[];
  optimized_url?: string | null;
  waveform_url?: string | null;
  has_waveform?: boolean;
  thumbnail?: string | null; // サムネイル画像URL（動画フレーム / PDF 1ページ目）
  /** ACR 非対応のファイル（個別 Chat の添付等）は null */
  acr_scan_status?: AcrScanStatus | null;
  acr_scan_result?: AcrScanResult | null;
  acr_scanned_at?: string | null;
}

export type DmMessageType = "user" | "system";

export type DmMessageFilterType = "all" | "unresolved" | "latest_unresolved";

// AI 校正（チクチクフィルター）/ 返信生成（返信君）の元/AI 両バージョン（投稿者本人のみ）。
export interface DmMessageProofread {
  original: string;
  corrected: string;
  active: "original" | "ai";
}

// 送信/更新時に渡す proofread ペイロード（comment 本文には常にアクティブ側が入る）。
export interface DmMessageProofreadPayload {
  original_comment?: string | null;
  ai_comment?: string | null;
  active_variant?: "original" | "ai" | null;
}

export interface DmMessage {
  id: string;
  message: string;
  type: DmMessageType;
  /**
   * システムメッセージの追加情報（Discriminated Union）。
   * `type === "system"` のときのみ値を持つ。BE 側は JSON 文字列で保存され、
   * API レスポンス段階でオブジェクトに展開される（dm-messages.md §概要 DB マッピング）。
   */
  system_content: DmSystemContent | null;
  author: DmMessageUser | null;
  parent_id: string | null;
  is_resolved: boolean;
  has_files: boolean;
  files: DmMessageFile[];
  receivers: DmMessageUser[];
  /** AI 校正/返信生成の両バージョン（投稿者本人のみ。未使用時は null/欠落） */
  proofread?: DmMessageProofread | null;
  created_at: string;
  updated_at: string;
}

export interface DmMessagesData {
  messages: DmMessage[];
  count: number;
  has_next: boolean;
  next_offset: number | null;
  limit: number;
  offset: number;
  filter: string;
  search: string | null;
}

export interface DmMessagesResponse {
  status: string;
  data: DmMessagesData | null;
}

export interface DmMessageDetailResponse {
  status: string;
  data: {
    message: DmMessage;
  };
}

// ─── クエリパラメータ ────────────────────────────────

export interface GetDmMessagesParams {
  project_id: string;
  dm_id: string;
  filter?: DmMessageFilterType;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface GetDmMessageDetailParams {
  project_id: string;
  dm_id: string;
  message_id: string;
}

// ─── mutation パラメータ / レスポンス ─────────────────

export interface SendDmMessageParams extends DmMessageProofreadPayload {
  project_id: string;
  dm_id: string;
  message: string;
  parent_id?: string | null;
  file_keys?: string[];
}

export interface SendDmMessageResponse {
  status: string;
  data: {
    message: DmMessage;
  };
}

export interface UpdateDmMessageParams extends DmMessageProofreadPayload {
  project_id: string;
  dm_id: string;
  message_id: string;
  message?: string;
  is_resolved?: boolean;
  file_keys?: string[];
}

export interface UpdateDmMessageResponse {
  status: string;
  data: {
    message: DmMessage;
  };
}

export interface DeleteDmMessageParams {
  project_id: string;
  dm_id: string;
  message_id: string;
}

export interface DeleteDmMessageResponse {
  status: string;
  message: string;
}

export interface MarkDmMessagesAsReadParams {
  project_id: string;
  dm_id: string;
  message_ids: string[];
}

export interface MarkDmMessagesAsReadResponse {
  status: string;
  data: {
    read_message_ids: string[];
  };
}

export interface MarkDmFilesAsDownloadedParams {
  project_id: string;
  dm_id: string;
  downloaded_file_ids: string[];
}

export interface MarkDmFilesAsDownloadedResponse {
  status: string;
  data: {
    downloaded_file_ids: string[];
  };
}

// ─── 内部ヘルパー ─────────────────────────────────────

/**
 * dmThreadsApi の `getDmThreads` キャッシュを走査し、
 * 対象 project_id 内で `dm_id` に一致するスレッドの `unread_count` を `delta` だけ減算する。
 *
 * RTK Toolkit 2.2 系には `util.selectCachedArgsForQuery` が存在しないため、
 * 代替として store state から直接キャッシュの `originalArgs` を取り出す。
 */
const decrementDmThreadUnreadCount = ({
  getState,
  dispatch,
  project_id,
  dm_id,
  delta,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  project_id: string;
  dm_id: string;
  delta: number;
}) => {
  const queries = getState()?.[dmThreadsApi.reducerPath]?.queries ?? {};
  type CachedQuery = {
    endpointName?: string;
    originalArgs?: { project_id?: string };
  };
  Object.values(queries).forEach((entry) => {
    const query = entry as CachedQuery | undefined;
    if (!query || query.endpointName !== "getDmThreads") return;
    const args = query.originalArgs;
    if (!args || args.project_id !== project_id) return;
    dispatch(
      dmThreadsApi.util.updateQueryData(
        "getDmThreads",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args as any,
        (draft) => {
          const thread = draft.data?.threads.find((t) => t.id === dm_id);
          if (!thread) return;
          thread.unread_count = Math.max(0, thread.unread_count - delta);
        },
      ),
    );
  });
};

// ─── RTK Query API ───────────────────────────────────

export const dmMessagesApi = createApi({
  reducerPath: "dmMessagesApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["DmMessages"],
  endpoints: (builder) => ({
    getDmMessages: builder.query<DmMessagesResponse, GetDmMessagesParams>({
      query: ({ project_id, dm_id, filter = "all", search, limit = 20, offset = 0 }) => {
        const params: Record<string, string> = {
          filter,
          limit: limit.toString(),
          offset: offset.toString(),
        };
        if (search) params.search = search;

        const queryString = new URLSearchParams(params).toString();
        return `/api/me/${project_id}/dm/${dm_id}/messages?${queryString}`;
      },
      providesTags: ["DmMessages"],
      keepUnusedDataFor: 30,
    }),

    getDmMessageDetail: builder.query<DmMessageDetailResponse, GetDmMessageDetailParams>({
      query: ({ project_id, dm_id, message_id }) =>
        `/api/me/${project_id}/dm/${dm_id}/messages/${message_id}`,
      providesTags: (_result, _error, arg) => [
        { type: "DmMessages", id: arg.message_id },
      ],
      keepUnusedDataFor: 60,
    }),

    // メッセージ送信
    // キャッシュ invalidate は行わず、呼び出し側で最新メッセージを取得・マージする
    sendDmMessage: builder.mutation<SendDmMessageResponse, SendDmMessageParams>({
      query: ({ project_id, dm_id, message, parent_id, file_keys, original_comment, ai_comment, active_variant }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/messages`,
        method: "POST",
        body: {
          message,
          ...(parent_id && { parent_id }),
          ...(file_keys && file_keys.length > 0 && { file_keys }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
    }),

    // メッセージ更新（本文 / is_resolved / 添付ファイル置換）
    updateDmMessage: builder.mutation<UpdateDmMessageResponse, UpdateDmMessageParams>({
      query: ({ project_id, dm_id, message_id, message, is_resolved, file_keys, original_comment, ai_comment, active_variant }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/messages/${message_id}`,
        method: "PATCH",
        body: {
          ...(message !== undefined && { message }),
          ...(is_resolved !== undefined && { is_resolved }),
          ...(file_keys !== undefined && { file_keys }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
    }),

    // メッセージ削除
    deleteDmMessage: builder.mutation<DeleteDmMessageResponse, DeleteDmMessageParams>({
      query: ({ project_id, dm_id, message_id }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/messages/${message_id}`,
        method: "DELETE",
      }),
    }),

    // 複数メッセージの既読登録
    // 成功時は dmThreadsApi キャッシュの unread_count を楽観的に減算して、
    // スレッド一覧の未読バッジを即時更新する。
    markDmMessagesAsRead: builder.mutation<MarkDmMessagesAsReadResponse, MarkDmMessagesAsReadParams>({
      query: ({ project_id, dm_id, message_ids }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/messages`,
        method: "PATCH",
        body: { message_ids },
      }),
      async onQueryStarted(
        { project_id, dm_id, message_ids },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          await queryFulfilled;
          decrementDmThreadUnreadCount({
            getState,
            dispatch,
            project_id,
            dm_id,
            delta: message_ids.length,
          });
        } catch {
          // 既読 API 失敗時はキャッシュを変更しない（次回 fetch で整合）
        }
      },
    }),

    // ファイルダウンロード記録
    markDmFilesAsDownloaded: builder.mutation<
      MarkDmFilesAsDownloadedResponse,
      MarkDmFilesAsDownloadedParams
    >({
      query: ({ project_id, dm_id, downloaded_file_ids }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/messages`,
        method: "PATCH",
        body: { downloaded_file_ids },
      }),
    }),
  }),
});

export const {
  useGetDmMessagesQuery,
  useLazyGetDmMessagesQuery,
  useGetDmMessageDetailQuery,
  useLazyGetDmMessageDetailQuery,
  useSendDmMessageMutation,
  useUpdateDmMessageMutation,
  useDeleteDmMessageMutation,
  useMarkDmMessagesAsReadMutation,
  useMarkDmFilesAsDownloadedMutation,
} = dmMessagesApi;
